import { z } from 'zod';
import { uuidSchema } from './common';

export const staffMemberSchema = z.object({
  membership_id: uuidSchema,
  user_id: uuidSchema,
  email: z.string(),
  display_name: z.string(),
  status: z.enum(['invited', 'active', 'deactivated']),
  roles: z.array(
    z.object({
      /** Role assignment id (revoke target); absent in older responses. */
      assignment_id: uuidSchema.optional(),
      role: z.string(),
      branch_id: uuidSchema.nullable(),
      branch_name: z.string().nullable(),
    }),
  ),
});
export type StaffMember = z.infer<typeof staffMemberSchema>;

export const staffListResponseSchema = z.object({ items: z.array(staffMemberSchema) });

/**
 * `POST …/staff` response. `invite_url` is set only for an e-mail invitation while e-mail
 * delivery is not live (`EMAIL_PROVIDER` `log`/`none`), so the manager can hand it over.
 */
export const createStaffResponseSchema = staffMemberSchema.extend({
  invite_url: z.string().nullable().optional(),
});
export type CreateStaffResponse = z.infer<typeof createStaffResponseSchema>;

/** System roles a manager can grant (`owner` is never granted through staff management). */
export const staffRoleSchema = z.enum([
  'manager',
  'cashier',
  'waiter',
  'host',
  'chef',
  'inventory',
  'warehouse_manager',
  'purchasing',
  'finance',
  'finance_manager',
  'auditor',
  'administrator',
]);
export type StaffRole = z.infer<typeof staffRoleSchema>;

export const createStaffRequestSchema = z.object({
  email: z
    .email()
    .max(254)
    .transform((e) => e.toLowerCase()),
  display_name: z.string().trim().min(1).max(120),
  /**
   * Initial password for new users (ignored if the e-mail already has an account). Omit it to
   * invite a new user by e-mail instead: they choose their own password (ADR-0031).
   */
  password: z.string().min(10).max(256).optional(),
  role: staffRoleSchema,
  /** null = all branches. */
  branch_id: uuidSchema.nullable(),
});

/**
 * `POST …/staff/:membershipId/roles` — add a role to an existing person. Roles add up, so a
 * small restaurant gives warehouse duties to a chef or cashier (ADR-0037).
 */
export const grantStaffRoleRequestSchema = z.object({
  role: staffRoleSchema,
  /** null = all branches. */
  branch_id: uuidSchema.nullable(),
});

export const updateBranchSettingsRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  name: z.string().trim().min(1).max(120).optional(),
  tax_rate_bps: z.number().int().min(0).max(5000).optional(),
  service_charge_bps: z.number().int().min(0).max(5000).optional(),
  /** Bill totals are rounded to this many minor units (1 = no rounding; IDR commonly 100). */
  rounding_unit_minor: z.number().int().min(1).max(100000).optional(),
  waste_approval_threshold_minor: z.number().int().min(0).refine(Number.isSafeInteger).optional(),
  refund_approval_threshold_minor: z.number().int().min(0).refine(Number.isSafeInteger).optional(),
  auto_sold_out: z.boolean().optional(),
  consumption_location_id: z.uuid().nullable().optional(),
  separation_of_duties: z.boolean().optional(),
  requisition_approval_threshold_minor: z
    .number()
    .int()
    .min(0)
    .refine(Number.isSafeInteger)
    .nullable()
    .optional(),
});
